import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { forgotPasswordStep1, forgotPasswordStep2, ValidateOtp } from '../Utils/Axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BriefcaseFill, EnvelopeFill, LockFill, UnlockFill } from 'react-bootstrap-icons';
import Loader from '../Utils/Loader';
import { Modal, ModalBody, ModalHeader, ModalTitle } from 'react-bootstrap';

const ForgotPassword = () => {
  const { register, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm({ 
    mode: "onChange", 
    defaultValues: {
      email: '',
      otp: '',
      password: '',
      confirmPassword: '',
    }, 
  });
  
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  const [otpShown, setOtpShown] = useState(false);
  const [otpTimeLimit, setOtpTimeLimit] = useState(56);
  const [otpExpired, setOtpExpired] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const watchPassword = watch('password', '');
  const navigate = useNavigate();
  
  // Get company from URL params
  const { company: urlCompany } = useParams();
  
  // Determine company value and navigation path
  const getCompanyValue = () => {
    // If URL has company parameter, use it (Company login)
    // If no URL company parameter, it's EMS login - use hardcoded "company"
    if (urlCompany) {
      console.log("Company Login - Using URL company:", urlCompany);
      return urlCompany;
    } else {
      console.log("EMS Login - Using hardcoded 'company'");
      return "company";
    }
  };

  const getNavigatePath = () => {
    const company = getCompanyValue();
    if (urlCompany) {
      // Company login - navigate to company login page
      return `/${company}/login`;
    } else {
      // EMS login - navigate to EMS login page
      return "/admin";
    }
  };

  useEffect(() => {
    if (otpTimeLimit > 0) {
      const timer = setTimeout(() => {
        setOtpTimeLimit((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setOtpExpired(true);
      setOtpShown(false);
      setStep(1);
      toast.error("Your OTP has expired. Please re-enter your email to get a new one.", {
        autoClose: 5000
      });
      reset({ otp: '' });
    }
  }, [otpTimeLimit]);

  const onSubmitEmail = async (data) => {
    setLoading(true);
    const company = getCompanyValue();
    const formData = {
      username: data.email,
      company: company
    };
    
    console.log("Forgot Password - Final Company:", company, "URL Company:", urlCompany);
    
    try {
      const response = await forgotPasswordStep1(formData);
      console.log(response.data);
      toast.success("OTP Sent Successfully");
      setEmail(data.email);
      setStep(2);
    } catch (error) {
      console.error("Failed to send OTP:", error);
      handleApiErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitOtp = async (data) => {
    setLoading(true);
    const company = getCompanyValue();
    const formData = {
      username: data.email,
      otp: data.otp,
      company: company,
    };

    console.log("Validate OTP - Final Company:", company, "URL Company:", urlCompany);
  
    try {
      const response = await ValidateOtp(formData);
      toast.success("Verification Successful");
      
      setStep(3);
      reset({
        otp: '',
        password: '',
        confirmPassword: '',
      });

      setOtpExpired(false);
      setOtpTimeLimit(40);
      setErrorMessage("");
    } catch (error) {
      handleApiErrors(error);
      reset({ otp: '' });
      setStep(1);
      setOtpTimeLimit(40);
      setOtpExpired(false);
      setLoading(false);
      setErrorMessage("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const onSubmitNewPassword = async (data) => {
    setLoading(true);
    const company = getCompanyValue();
    
    console.log("Update Password - Final Company:", company, "URL Company:", urlCompany);
    
    try {
      const formData = {
        username: email,
        password: data.password,
        company: company,
        companyFullName: data.companyName,
      };
      await forgotPasswordStep2(formData);
      toast.success("Password Updated Successfully");
      reset();
      navigate(getNavigatePath());
    } catch (error) {
      handleApiErrors(error);
    } finally {
      setLoading(false);
    }
  };  

  const handleApiErrors = (error) => {
    if (error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
      const errorMessage = error.response.data.error.message;
      toast.error(errorMessage);
    } else {
      toast.error("Network Error !");
    }
    console.error(error.response);
  };

  const togglePasswordVisibility = () => {
    setPasswordShown(!passwordShown);
  };
  
  const confirmPasswordVisibility = () => {
    setConfirmPasswordShown(!confirmPasswordShown);
  };

  const toggleOtpVisibility = () => {
    setOtpShown(!otpShown);
  };

  const closeButton = () => {
    reset();
    setOtpTimeLimit(40);
    navigate(getNavigatePath());
  };

  const closeModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const validatePassword = (value) => {
    const errors = [];
    if (!/(?=.*[0-9])/.test(value)) {
      errors.push("at least one digit");
    }
    if (!/(?=.*[a-z])/.test(value)) {
      errors.push("at least one lowercase letter");
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      errors.push("at least one uppercase letter");
    }
    if (!/(?=.*[\W_])/.test(value)) {
      errors.push("at least one special character");
    }
    if (value.includes(" ")) {
      errors.push("no spaces");
    }

    if (errors.length > 0) {
      return `Password must contain ${errors.join(", ")}.`;
    }
    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    if (e.keyCode === 32) {
      e.preventDefault();
    }
    if (value.includes(" ")) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData('Text');
    const sanitizedText = pastedText.replace(/[^A-Za-z0-9]/g, '');
    e.preventDefault();
    e.target.value = sanitizedText;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <form onSubmit={handleSubmit(onSubmitEmail)}>
            <div className="form-group mb-1">
              <label>Email Id</label>
              <div className="input-group">
                <span className="input-group-text">
                  <EnvelopeFill size={20} color="#4C489D" />
                </span>
                <input
                  type="email"
                  placeholder='Enter Your Email Id'
                  className="form-control"
                  onKeyDown={handleEmailChange}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[a-z][a-zA-Z0-9._+-]*@[a-zA-Z0-9.-]+\.(com|in|org|net|edu|gov)$/,
                      message: "Invalid email format",
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="errorMsg p-0" style={{ marginLeft: "60px" }}>
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="text-center mt-4">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Loading..." : "Send OTP"}
              </button>
            </div>
          </form>
        );
      case 2:
        return (
          <form onSubmit={handleSubmit(onSubmitOtp)}>
            <div className="form-group mb-1">
              <label>Email Id:</label>
              <div className="input-group">
                <span className="input-group-text">
                  <EnvelopeFill size={20} color="#4C489D" />
                </span>
                <input type="email" className="form-control" value={email} disabled />
              </div>
            </div>
            <div className="form-group">
              <label>OTP:</label>
              <div className="input-group">
                <span className="input-group-text" onClick={toggleOtpVisibility} style={{ cursor: 'pointer' }}>
                  {otpShown ? (
                    <UnlockFill size={20} color="#4C489D" />
                  ) : (
                    <LockFill size={20} color="#4C489D" />
                  )}
                </span>
                <input
                  type={otpShown ? "text" : "password"}
                  placeholder='Enter OTP'
                  className="form-control"
                  onKeyDown={handleEmailChange}
                  onPaste={handlePaste}
                  maxLength={6}
                  inputMode="numeric"
                  {...register('otp', {
                    required: 'OTP is required',
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: 'Invalid OTP'
                    },
                    maxLength: {
                      value: 6,
                      message: "Maximum 6 Digits Allowed",
                    },
                  })}
                />
              </div>
              {errors.otp && <span className="text-danger" style={{ marginLeft: "60px" }}>{errors.otp.message}</span>}
            </div>
            <div className="text-center mt-4">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Loading..." : "Submit"}
              </button>
            </div>
          </form>
        );
      case 3:
        return (
          <form onSubmit={handleSubmit(onSubmitNewPassword)}>
            <div className="form-group mb-1">
              <label>Email:</label>
              <div className="input-group">
                <span className="input-group-text">
                  <EnvelopeFill size={20} color="#4C489D" />
                </span>
                <input type="email" className="form-control" value={email} disabled />
              </div>
            </div>
            <div className="form-group">
              <label>New Password:</label>
              <div className="input-group">
                <span className="input-group-text" onClick={togglePasswordVisibility}>
                  {passwordShown ? (
                    <UnlockFill size={20} color="#4C489D" />
                  ) : (
                    <LockFill size={20} color="#4C489D" />
                  )}
                </span>
                <input
                  placeholder='Enter Your New Password'
                  type={passwordShown ? "text" : "password"}
                  className="form-control"
                  name='password'
                  onPaste={handlePaste}
                  onKeyDown={handleEmailChange}
                  {...register("password", {
                    required: "Password is Required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters long",
                    },
                    maxLength: {
                      value: 16,
                      message: "Password must be at least 16 characters long",
                    },
                    validate: validatePassword,
                  })}
                />
              </div>
              {errors.password && (
                <p className="errorMsg" style={{ marginLeft: "55px", marginBottom: "0" }}>
                  {errors.password.message}
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Confirm Password:</label>
              <div className="input-group">
                <span className="input-group-text" onClick={confirmPasswordVisibility}>
                  {confirmPasswordShown ? (
                    <UnlockFill size={20} color="#4C489D" />
                  ) : (
                    <LockFill size={20} color="#4C489D" />
                  )}
                </span>
                <input
                  placeholder='Confirm Password'
                  type={confirmPasswordShown ? "text" : "password"}
                  className="form-control"
                  onKeyDown={handleEmailChange}
                  onPaste={handlePaste}
                  name='confirmPassword'
                  {...register('confirmPassword', {
                    required: 'Confirm Password is required',
                    validate: value => value === watchPassword || "Passwords do not match",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters long",
                    },
                    maxLength: {
                      value: 16,
                      message: "Password must be at least 16 characters long",
                    },
                  })}
                />
              </div>
              {errors.confirmPassword && (
                <p className="errorMsg" style={{ marginLeft: "60px", marginBottom: "0" }}>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div className="text-center mt-4">
              <button type="submit" className="btn btn-primary" disabled={loading || isSubmitting}>
                {loading ? "Loading..." : "Update Password"}
              </button>
            </div>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <main className="d-flex w-100 ">
      {loading && <Loader />}
      <div className="container d-flex flex-column">
        <div className="row vh-100">
          <div className="col-sm-10 col-md-7 col-lg-6 mx-auto d-table h-100">
            <div className="d-table-cell align-middle">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center pb-0">
                  <div className="text-center">
                    <p className="lead">Forgot Password</p>
                  </div>
                  <button className='btn text-dark' style={{ marginBottom: "6px" }} onClick={closeButton}>X</button>
                </div>

                <div className="card-body" style={{ marginRight: '60px', paddingTop: '0px' }}>
                  <div className="mt-2 m-sm-2 ">
                    {renderStep()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showErrorModal}
        onHide={closeModal}
        centered
        style={{ zIndex: "1050" }}
        className="custom-modal"
      >
        <ModalHeader>
          <ModalTitle className="text-center">Error</ModalTitle>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={closeModal}
          ></button>
        </ModalHeader>
        <ModalBody className="text-center fs-bold">
          {errorMessage}
        </ModalBody>
      </Modal>
    </main>
  );
};

export default ForgotPassword;